import { hideOnError } from "@/lib/img-fallback";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { RotatingWord } from "@/components/ui/rotating-word";
import { HQ_PHONE_DISPLAY, HQ_PHONE_TEL } from "@/config/contact";
import { INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";
import { lazySection } from "@/lib/lazy-section";
import { testimonialsQueryOptions, casesByTagQueryOptions } from "@/lib/public-content.queries";
import rhetorikAsset from "@/assets/rhetorik.webp.asset.json";
import akutbegleitungAsset from "@/assets/akutbegleitung.png.asset.json";
import auditAsset from "@/assets/audit-2.png.asset.json";
import monitoringAsset from "@/assets/monitoring-arrows.svg.asset.json";
import mikrofoneAsset from "@/assets/mikrofone.png.asset.json";
import bewertungAsset from "@/assets/bewertung.svg.asset.json";
import justiciaAsset from "@/assets/justicia.svg.asset.json";
import michaelBusserPortrait from "@/assets/michael-busser-quadratisch.png.asset.json";

import { ScrollRevealText } from "@/components/ScrollRevealText";
import { WappenAnimation } from "@/components/krisenkommunikation/WappenAnimation";
import { KontrollverlustGrafik } from "@/components/krisenkommunikation/KontrollverlustGrafik";
import { WolkenBackdrop } from "@/components/krisenkommunikation/WolkenBackdrop";
import { Search, PenTool, Rocket, ArrowUpRight } from "lucide-react";
import type { Step } from "@/components/buergermeisterwahl/ProzessTimeline";

const CasesSection = lazySection(async () => {
  const m = await import("@/components/cases/CasesSection");
  return { default: m.CasesSection };
}, { minHeight: 480 });
const ContactCta = lazySection(async () => {
  const m = await import("@/components/krisenkommunikation/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 480 });

const PROZESS_STEPS: Step[] = [
  {
    title: "Lage-Analyse",
    text: "Wir identifizieren Ihre kommunikativen Schwachstellen und analysieren das aktuelle Bedrohungspotenzial für Ihre Organisation. Vertraulich und ohne Umwege.",
    Icon: Search,
  },
  {
    title: "Strategie-Design",
    text: "Wir entwerfen ein belastbares Narrativ und einen Maßnahmenplan, um die Deutungshoheit zu sichern oder zurückzugewinnen. Präzise auf Ihre Organisation zugeschnitten und transparent kalkuliert.",
    Icon: PenTool,
  },
  {
    title: "Umsetzung",
    text: "Sie entscheiden über den Weg. Wir begleiten Sie als erfahrener Partner bei der operativen Ausführung: vom einzelnen Statement bis zur vollständigen Krisenbewältigung.",
    Icon: Rocket,
  },
];



export const Route = createFileRoute("/politisch-kommunizieren/krisenkommunikation")({
  // Testimonials und Cases speisen nur lazy nachgeladene Below-the-Fold-Sektionen
  // → Prefetch anstoßen, aber nicht awaiten (kürzere TTFB / kritische Kette).
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    void context.queryClient.prefetchQuery(casesByTagQueryOptions("krisenkommunikation"));
  },
  head: () => ({
    meta: [
      { title: "Krisenkommunikation | creategy" },
      {
        name: "description",
        content:
          "Wenn es darauf ankommt, zählt jede Stunde: Wir halten politische Akteure und Organisationen in kritischen Lagen handlungsfähig. Schnell und diskret.",
      },
      { property: "og:title", content: "Krisenkommunikation | creategy" },
      {
        property: "og:description",
        content:
          "Wenn es darauf ankommt, zählt jede Stunde: Wir halten politische Akteure und Organisationen in kritischen Lagen handlungsfähig. Schnell und diskret.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://creategy.de/politisch-kommunizieren/krisenkommunikation" },
      { property: "og:site_name", content: "creategy" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Krisenkommunikation | creategy" },
      {
        name: "twitter:description",
        content:
          "Wenn es darauf ankommt, zählt jede Stunde: Wir halten politische Akteure und Organisationen in kritischen Lagen handlungsfähig. Schnell und diskret.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://creategy.de/politisch-kommunizieren/krisenkommunikation" },
    ],
  }),
  component: KrisenkommunikationPage,
});

function KrisenkommunikationPage() {



  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero" className="relative">
        <WolkenBackdrop />
        <div className="section-y-hero relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="relative max-w-4xl">
            <h1
              id="hero"
              className="heading-h1 relative z-10 text-brand-kommunikation"
            >
              Wir übernehmen, wenn{" "}
              <RotatingWord
                className="!inline md:!inline-block"
                words={[
                  "die Presse anruft.",
                  "der Shitstorm tobt.",
                  "die Debatte kippt.",
                  "die Krise da ist.",
                ]}
              />
            </h1>
            <p
              className="fade-in-up relative z-10 mt-10 font-body text-base leading-relaxed text-foreground md:text-lg"
              style={{ animationDelay: "160ms" }}
            >
              Wenn Debatten Ihr Geschäftsmodell bedrohen: Wir sichern die gesellschaftliche Akzeptanz von Branchen, Unternehmen und NGOs. Präventiv, im Ernstfall und danach.
            </p>
            <div
              className="fade-in-up relative z-10 mt-10 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center"
              style={{ animationDelay: "240ms" }}
            >
              <a
                href={`tel:${HQ_PHONE_TEL}`}
                className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 sm:w-auto"
              >
                Jetzt anrufen: {HQ_PHONE_DISPLAY}
              </a>
              <Link
                to="/kontakt"
                search={{ anliegen: "krisenkommunikation" }}
                className="inline-flex w-full items-center justify-center rounded-md border border-primary px-6 py-3 font-display text-sm font-extrabold text-primary transition-colors hover:bg-primary/5 sm:w-auto"
              >
                {INQUIRY_CTA_LABEL}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Dringlichkeit */}
      <section aria-labelledby="dringlichkeit">
        <h2 id="dringlichkeit" className="sr-only">Wie dringend ist es?</h2>
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-6 md:grid-cols-2 md:items-stretch">
            <Reveal className="reveal-soft">
              <article className="flex h-full flex-col rounded-md border-2 border-brand-accent p-6 md:p-8">
                <h3 className="heading-h3 text-brand-kommunikation">Es ist bereits passiert.</h3>
                <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                  Shitstorm, Presseanfrage, Leak? Rufen Sie uns an. Wir sind kurzfristig erreichbar und besprechen sofort die ersten Schritte.
                </p>
                <a
                  href={`tel:${HQ_PHONE_TEL}`}
                  className="mt-6 inline-block font-display text-3xl font-extrabold text-brand-accent transition-colors hover:text-primary md:text-4xl"
                >
                  {HQ_PHONE_DISPLAY}
                </a>
                <p className="mt-2 font-body text-sm text-muted-foreground">
                  Direkter Draht, kein Ticketsystem.
                </p>
              </article>
            </Reveal>
            <Reveal delay={60} className="reveal-soft">
              <article className="flex h-full flex-col rounded-md border border-border p-6 md:p-8">
                <h3 className="heading-h3 text-brand-kommunikation">Ich will vorbereitet sein.</h3>
                <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                  Die beste Krise ist die, die Sie souverän kommen sehen. Erfahren Sie, wie wir Ihre Organisation krisenfest machen.
                </p>
                <a
                  href="#bausteine"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("bausteine")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="mt-auto pt-6 inline-flex items-center gap-1.5 font-display text-sm font-extrabold text-brand-kommunikation transition-opacity hover:opacity-80"
                >
                  Zur Krisenprävention
                  <ArrowUpRight className="h-4 w-4" aria-hidden />
                </a>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Kontrollverlust */}
      <section aria-labelledby="kontrollverlust">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div>
            <Reveal>
              <div className="mb-8 w-full md:hidden">
                <KontrollverlustGrafik />
              </div>
            </Reveal>
            <Reveal>
              <h2
                id="kontrollverlust"
                className="heading-h2 text-brand-kommunikation"
              >
                Die eigentliche Krise ist der Kontrollverlust.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="heading-lead grid gap-12 md:grid-cols-2 md:items-start">
                <div>
                  <p className="font-body text-base leading-relaxed text-foreground md:text-lg">
                    Ein kritischer Bericht, ein kippender Diskurs, ein interner Konflikt, der öffentlich wird: Das Problem selbst ist selten das, was Organisationen in Bedrängnis bringt. Gefährlich wird der Moment, in dem die Kontrolle über das eigene Narrativ verloren geht. Dann bestimmen andere, wer Sie sind und wofür Sie stehen.
                  </p>
                  <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                    Organisationen stehen heute unter Dauerbeobachtung. Wer in diesem Umfeld den Kopf einzieht, verliert die Lizenz zum Handeln. Wir sorgen dafür, dass Sie sprechfähig bleiben: mit belastbaren Narrativen, klaren Abläufen und einem Team, das Krisenkommunikation auf höchster Ebene erlebt hat.
                  </p>
                </div>
                <div className="hidden w-full md:block md:sticky md:top-24 md:self-start">
                  <KontrollverlustGrafik />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Wen wir schützen */}
      <section aria-labelledby="wen-wir-schuetzen">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="wen-wir-schuetzen"
              className="heading-h2 text-brand-kommunikation"
            >
              Wen wir schützen.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Wir unterstützen Akteure, die unter wirtschaftlichem und gesellschaftlichem Rechtfertigungsdruck stehen. Sehen Sie in uns Ihren Reputations-Architekten: proaktiv, diskret und politisch erfahren.
            </p>
          </Reveal>
          <div className="mt-12 grid items-start gap-10 md:grid-cols-2 md:gap-16">
            <div className="md:order-2 lg:order-1 md:sticky md:top-24 md:self-start">
              <WappenAnimation />
            </div>
            <div className="md:order-1 lg:order-2 flex flex-col gap-8">
              {[
                {
                  title: "Branchen-Akzeptanz für Verbände.",
                  text: "Ganze Industrien stehen unter Rechtfertigungsdruck. Ob Chemie, Fleischwirtschaft oder Immobilien: Wenn der öffentliche Diskurs kippt, drohen regulative Einschnitte. Wir halten Ihre Branche sprechfähig, bevor die Politik Fakten schafft.",
                },
                {
                  title: "Shitstorm-Schutz für Unternehmen.",
                  text: "Ein neues Verpackungsdesign, der Bau eines Logistikzentrums, der Wasserverbrauch in der Produktion: Lokale Themen werden heute global skaliert und politisiert. Wir sorgen dafür, dass aus einem Aufreger keine Existenzfrage wird.",
                },
                {
                  title: "Vertrauensschutz für NGOs.",
                  text: "Für NGOs ist Vertrauen die einzige Währung. Transparenzfragen oder interne Konflikte können die Spendenbereitschaft über Nacht vernichten. Wir schützen, was Ihre Organisation trägt: ihre Glaubwürdigkeit.",
                },
              ].map((c, i) => (
                <Reveal key={c.title} delay={i * 60} className="reveal-soft">
                  <article>
                    <h3 className="heading-h3 text-brand-kommunikation">{c.title}</h3>
                    <p className="mt-4 font-body text-sm leading-relaxed text-foreground">
                      {c.text}
                    </p>
                  </article>
                </Reveal>
              ))}
            </div>

          </div>
        </div>
      </section>


      {/* Bausteine */}
      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-kommunikation"
            >
              Die Bausteine Ihrer Krisenfestigkeit.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Krisenkommunikation beginnt lange vor der Krise und endet nicht mit der letzten Schlagzeile. Sie buchen bei uns genau die Unterstützung, die Ihre Lage erfordert: vorbereitend, akut oder im Wiederaufbau.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {[
              {
                title: "Krisenprävention & Audit",
                text: "Wo ist Ihre Organisation verwundbar? Wir analysieren Ihre Schwachstellen, entwickeln Ihr Krisenhandbuch und bereiten Sie für den Ernstfall vor.",
                image: auditAsset.url,
                imageAlt: "Mann im weißen Hemd konzentriert am Laptop",
              },
              {
                title: "Akutbegleitung & Krisenstab",
                text: "Wenn es brennt, zählen Stunden. Wir übernehmen die kommunikative Lageführung, entwickeln Statements und stehen Ihrem Krisenstab zur Seite, bis die Lage beherrscht ist.",
                image: akutbegleitungAsset.url,
                imageAlt: "Zwei Berater von creategy im akuten Krisenstab",
                imageClassName: "block h-auto max-h-[11rem] md:max-h-[10rem] lg:max-h-[14rem] w-auto max-w-full object-contain object-bottom",
              },
              {
                title: "Medientraining für den Ernstfall",
                text: "Das Interview, die Pressekonferenz\u00a0 die Betriebsversammlung: Regierungssprecher a.D. Michael Bußer bereitet Ihre Sprecherinnen und Sprecher darauf vor.",
                image: mikrofoneAsset.url,
                imageAlt: "Mehrere Hände halten Mikrofone in Richtung eines Sprechers",
                layout: "split" as const,
              },

              {
                title: "Monitoring & Issue Management",
                text: "Krisen kündigen sich an, wenn man hinhört. Wir beobachten Debatten rund um Ihre Themen und erkennen kritische Entwicklungen, bevor sie eskalieren.",
                image: monitoringAsset.url,
                imageAlt: "Pfeile als Symbol für Monitoring und Issue Management",
                imageClassName: "block h-auto max-h-[8rem] w-auto max-w-full object-contain object-bottom md:max-h-[10rem]",
              },
              {
                title: "Kommunikation in Verfahren",
                text: "Wenn juristische Auseinandersetzungen öffentlich werden, braucht es Kommunikation, die den Prozess nicht gefährdet und die Reputation trotzdem schützt.\u00a0",
                image: justiciaAsset.url,
                imageAlt: "Illustration Justitia mit Waage",
                imageClassName: "block h-auto max-h-[11rem] md:max-h-[10rem] lg:max-h-[14rem] w-auto max-w-full object-contain object-bottom",
              },
              {
                title: "Reputationsaufbau nach der Krise",
                text: "Nach der Krise beginnt die eigentliche Arbeit. Wir helfen Ihnen, verlorenes Vertrauen systematisch zurückzugewinnen.",
                image: bewertungAsset.url,
                imageAlt: "Fünf-Sterne-Bewertung",
                layout: "centered" as const,
              },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 60} className="lg:col-span-2 h-full">
                <article className="flex h-[20rem] flex-col overflow-hidden rounded-md bg-brand-kommunikation p-6 text-background md:h-[24rem] md:p-8">
                  <h3 className="heading-h3 text-background">
                    {b.title}
                  </h3>
                  {b.layout === "split" && b.image ? (
                    <div className="mt-4 grid min-h-0 flex-1 grid-cols-2 gap-4">
                      <p className="font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                        {b.text}
                      </p>
                      <div className="-mr-6 -mb-10 flex items-end justify-end md:-mr-8 md:-mb-12">
                        <img
                          src={b.image}
                          alt={b.imageAlt ?? ""}
                          className={b.imageClassName ?? "block h-auto max-h-[13rem] md:max-h-[13rem] lg:max-h-[17rem] w-auto max-w-full object-contain object-bottom"}
                          decoding="async" onError={hideOnError}
                        />
                      </div>
                    </div>
                  ) : b.layout === "centered" && b.image ? (
                    <>
                      <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                        {b.text}
                      </p>
                      <div className="mt-auto mb-4 flex justify-center md:mb-6">
                        <img
                          src={b.image}
                          alt={b.imageAlt ?? ""}
                          className={b.imageClassName ?? "block h-auto max-h-14 md:max-h-[4.5rem] lg:max-h-[4.5rem] w-auto max-w-full object-contain"}
                          decoding="async" onError={hideOnError}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                        {b.text}
                      </p>
                      {b.image ? (
                        <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end justify-center md:-mx-8 md:-mb-12">
                          <img
                            src={b.image}
                            alt={b.imageAlt ?? ""}
                            className={b.imageClassName ?? "block h-auto max-h-[11rem] w-auto max-w-full object-contain object-bottom md:max-h-[14rem]"}
                            decoding="async" onError={hideOnError}
                          />
                        </div>
                      ) : (
                        <div
                          aria-hidden="true"
                          className="mt-auto flex aspect-[4/3] items-center justify-center rounded-md border border-dashed border-border bg-secondary/40"
                        >
                          <span className="font-display text-xs uppercase tracking-wider text-muted-foreground">
                            Bild folgt
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </article>
              </Reveal>

            ))}
          </div>
        </div>
      </section>

      {/* Michael Bußer Spotlight */}
      <section aria-labelledby="michael-busser-krise">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="michael-busser-krise" className="heading-h2 text-brand-kommunikation">
              Expertise, die den Unterschied macht.
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Unter der Leitung von Michael Bußer, ehemaliger Regierungssprecher und Staatssekretär a.D., entwickelt unser Team präzise Strategien für komplexe kommunikative Herausforderungen. Michael Bußer hat in Situationen kommuniziert, in denen jedes Wort auf der Goldwaage lag: in Regierungskrisen, unter laufenden Kameras und gegenüber einer kritischen Hauptstadtpresse.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 grid gap-8 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)] md:items-center">
              <div className="mx-auto w-full max-w-[320px] overflow-hidden rounded-md bg-transparent md:mx-0">
                <img
                  src={michaelBusserPortrait.url}
                  alt="Michael Bußer"
                  className="h-auto w-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col justify-center">
                <ScrollRevealText
                  text={'„In der Krise gibt es keine zweite Chance für den ersten Satz. Wer vorbereitet ist, kommuniziert souverän. Wer souverän kommuniziert, behält die Deutungshoheit."'}
                  className="font-body text-xl leading-snug text-brand-kommunikation md:text-2xl lg:text-3xl"
                />
                <p className="mt-6 font-display text-sm font-extrabold text-brand-kommunikation">
                  Michael Bußer
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mandate / Vignetten */}
      <section aria-labelledby="mandate">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="mandate" className="heading-h2 text-brand-kommunikation">
              Mandate, über die wir nicht sprechen.
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Diskretion ist Teil unseres Mandats. Unsere Kundinnen und Kunden werden Sie hier deshalb nicht finden. Was wir zeigen können: die Art von Lagen, durch die wir Organisationen begleitet haben.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
              {[
                {
                  title: "Bundesverband, kritische Branche",
                  text: "Eine geplante EU-Verordnung drohte das Geschäftsmodell einer ganzen Branche infrage zu stellen. Wir haben den Verband über acht Monate durch die Regulierungsdebatte begleitet.",
                },
                {
                  title: "Mittelständisches Unternehmen, Konsumgüter",
                  text: "Eine lokale Standortentscheidung wurde in den sozialen Medien aufgegriffen und binnen 48 Stunden bundesweit skaliert. Wir haben die kommunikative Lageführung übernommen.",
                },
                {
                  title: "NGO,\nbundesweit tätig",
                  text: "Ein interner Konflikt drohte öffentlich zu werden und das Spendervertrauen zu beschädigen. Wir haben die Organisation durch eine transparente Aufarbeitung begleitet.",
                },
                {
                  title: "Energieunternehmen, Infrastrukturprojekt",
                  text: "Ein Ausbauvorhaben stieß auf organisierten lokalen Widerstand mit überregionaler Medienresonanz. Wir haben Bürgerdialog, Pressearbeit und digitale Kommunikation aus einer Hand gesteuert.",
                },
                {
                  title: "Verband,\nGesundheitswesen",
                  text: "Ein kritischer Fernsehbeitrag stellte die Integrität einer ganzen Berufsgruppe infrage. Innerhalb von 24 Stunden standen Statement, Q&A und Mitgliederinformation.\u00a0",
                },
                {
                  title: "Kommunaler Akteur, Großprojekt",
                  text: "Vor einer konfliktträchtigen Bürgerbeteiligung haben wir Verwaltung und Projektspitze auf kritische Fragen, Störungen und Eskalationsszenarien vorbereitet.",
                },
              ].map((v) => (
                <article
                  key={v.title}
                  className="flex h-full flex-col rounded-md border border-border p-6 md:p-8"
                >
                  <h3 className="heading-h3 text-balance text-brand-kommunikation">{v.title}</h3>
                  <p className="mt-4 font-body text-base leading-relaxed text-foreground">
                    {v.text}
                  </p>
                </article>
              ))}
            </div>
          </Reveal>
          <Reveal delay={180}>
            <p className="mt-10 font-body text-base leading-relaxed text-foreground md:mt-12 md:text-lg">
              Was alle Mandate verbindet: Kein Fall ist an die Öffentlichkeit gelangt, der es nicht ohnehin schon war.
            </p>
          </Reveal>
        </div>
      </section>


      {/* Prozess */}
      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess" className="heading-h2 text-brand-kommunikation">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline headingColor="text-brand-kommunikation" steps={PROZESS_STEPS} />
          </div>

          <Reveal>
            <p className="mt-16 font-body text-sm leading-relaxed text-foreground/80 md:text-base">
              Im Ernstfall beginnt Schritt 1 am Telefon. Rufen Sie uns an, wir sind kurzfristig für Sie da.
            </p>
          </Reveal>
        </div>
      </section>









      {/* Cases */}
      <CasesSection tag="krisenkommunikation" />
      <ContactCta />

    </Layout>
  );
}
